/**
 * `SelectField` — the application's ONE `<select>` primitive (U3.3).
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⚠ IT USED TO LIVE IN `features/inquiries/panel.tsx`, AND THAT WAS A REAL PROBLEM
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * Four features imported it across a feature boundary — quotes, admin, crfs and inquiries
 * itself — while `components/ui` had no Select primitive at all. Three consequences, and
 * the third is the one that was about to bite (OQ-SELECTFIELD-UI):
 *
 *   1. The import graph said something FALSE. `features/admin` depending on
 *      `features/inquiries` reads as a domain relationship. There is none — the catalogue
 *      screen needs a dropdown.
 *   2. `inquiries/` could not be refactored freely: anyone reorganising that feature was
 *      silently editing a shared primitive used by the quote builder and the catalogue,
 *      and nothing in `panel.tsx` said so.
 *   3. THE PRESSURE WAS TOWARD A SECOND IMPLEMENTATION. U3 needed `<optgroup>` in the quote
 *      picker and the only ways to get it were to edit another feature's file from a
 *      different stream, or to fork a second select. One control with two implementations
 *      is a rule plus a future disagreement about how a customer-facing form behaves.
 *
 * ⚠ NO RE-EXPORT SHIM WAS LEFT IN `panel.tsx`, DELIBERATELY. A shim would keep the false
 * import graph alive while looking like the refactor had happened — which is the specific
 * warning OQ-SELECTFIELD-UI ends on. Every importer was re-pointed instead, and
 * `select-field-placement.test.ts` fails if `features/inquiries` ever exports it again.
 *
 * There is NO behaviour in this move. The component, its markup and `optionRuns` are
 * byte-identical to what shipped; the whole point of giving it its own unit was that a
 * rename with no behaviour in it should be reviewable as exactly that.
 */

import { ChevronDown } from 'lucide-react';

import { cn } from '../../lib/cn';
import { optionRuns } from './select-runs';
import type { SelectFieldOption } from './select-runs';

/**
 * The shared field chrome — border, radius, surface, placeholder colour.
 *
 * ⚠ EXPORTED RATHER THAN COPIED. `TextAreaField` in `features/inquiries/panel.tsx` uses the
 * same box, and two string literals that must stay identical are the drift nobody notices
 * until one control has a different border in one screen.
 */
export const FIELD_BOX =
  'flex w-full items-center gap-2 rounded-sm border border-line bg-subtle px-3 text-body text-fg outline-none placeholder:text-fg-muted';

export function SelectField({
  label,
  hint,
  options,
  value,
  onChange,
  id,
}: {
  label: string;
  hint?: string;
  options: readonly SelectFieldOption[];
  value: string;
  onChange: (value: string) => void;
  id?: string;
}): React.JSX.Element {
  const fieldId = id ?? `select-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-label font-medium text-fg">
        {label}
      </label>
      <div className={cn(FIELD_BOX, 'relative h-9')}>
        <select
          id={fieldId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          // A native <select>: it is keyboard- and screen-reader-correct for free, and on
          // a phone it opens the platform picker. The chevron is decorative and sits over
          // it, matching `Input/Select`'s trailing glyph.
          className="w-full appearance-none bg-transparent pr-6 text-body text-fg outline-none"
        >
          {/*
           * The branch is the guarantee (U3.4). With no option carrying a `group` this is
           * the identical flat render it has always been — `<optgroup>` is never emitted,
           * so no caller that does not group can be changed by this.
           *
           * When they DO group, the options are emitted in RUNS, in the order the caller
           * supplied. Sorting them here would reorder a picker the server already ordered
           * (country, shelf, sort_order, code) and would put the client and the printed
           * quote into two different orders.
           */}
          {options.every((option) => option.group === undefined)
            ? options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            : optionRuns(options).map((run, index) =>
                run.group === undefined ? (
                  run.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <optgroup key={`${run.group}-${String(index)}`} label={run.group}>
                    {run.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ),
              )}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 size-3.5 text-fg-secondary"
        />
      </div>
      {hint === undefined ? null : <p className="text-label text-fg-muted">{hint}</p>}
    </div>
  );
}
